import React, { useContext } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { ThemeContext } from 'styled-components';
// Import Shared Components
import { Checkbox } from 'shared-components';
// Import Local Utils
import { activationConsents, activationConsentsAccepted } from '../../utils/activationConsents';
import { errorMessage } from '../../utils/validationHandlers';
// Import Hooks
import { ACTIVATION_PHASES } from '../../hooks/useCardActivation';
// The activation page's fields and agreement links, so both ask the same way.
import CardFields from '../../routes/Auth/routes/components/CardFields';
import CardholderAgreementLink from '../../routes/Auth/routes/components/CardholderAgreementLink';
import PrivacyPolicyAgreementLink from '../../routes/Auth/routes/components/PrivacyPolicyAgreementLink';
import ElectronicComConsentLink from '../../routes/Auth/routes/components/ElectronicComConsentLink';
import { FormRow } from '../../routes/Auth/styles';
// Import Component Styles
import {
  Scrim,
  Dialog,
  StepBody,
  CloseButton,
  Title,
  Banner,
  Hint,
  Actions,
  FlowButton,
  Centered,
  Spinner,
  SuccessMark,
} from './styles';
import { CheckMark } from './ReplaceCardModal';
import useDialogFocus from './useDialogFocus';

// The consent checkboxes the white label asks for, in the activation page's order.
const CONSENT_ROWS = [
  { key: 'electronicCom', id: 'activate-electronic-com-consent-label', link: ElectronicComConsentLink },
  { key: 'terms', id: 'activate-terms-consent-label' },
  { key: 'cardholderAgreement', id: 'activate-cardholder-agreement-label', link: CardholderAgreementLink },
  { key: 'privacyPolicy', id: 'activate-privacy-policy-label', link: PrivacyPolicyAgreementLink },
];

// Activates the replacement card from the dashboard without leaving the session. The fields,
// checks, consents and endpoint are the activation page's; the dialog is the replace-card one.
const ActivateCardModal = ({
  isOpen,
  phase,
  card,
  errors,
  consents,
  formError,
  onChange,
  onBlur,
  onConsent,
  onSubmit,
  onClose,
}) => {
  const intl = useIntl();
  const theme = useContext(ThemeContext);
  // No closing with Escape while the card is being activated.
  const dialogRef = useDialogFocus({
    isOpen,
    onClose,
    canClose: phase !== ACTIVATION_PHASES.SUBMITTING,
  });

  if (!isOpen) return null;

  const brand = theme && theme.brand;
  const asked = activationConsents(brand);
  const submitting = phase === ACTIVATION_PHASES.SUBMITTING;
  const canSubmit = activationConsentsAccepted(brand, consents);

  const actionButton = (key, onClick, tone = 'secondary', props = {}) => (
    <FlowButton
      buttonText={intl.messages[`activate-card-${key}`]}
      page="activate-card"
      buttonFor={key}
      $tone={tone}
      onClick={onClick}
      {...props}
    />
  );

  const submit = () => {
    if (canSubmit) onSubmit(intl.messages);
  };

  // Not a <form>: the portal Button takes type for its colour, so it cannot be a submit
  // button; Activate is pressed, as in the replace-card dialog.
  const renderForm = () => (
    <>
      <Title id="activate-card-title">{intl.messages['activate-card-heading']}</Title>
      <Hint as="p" $size="14px">{intl.messages['activate-card-intro']}</Hint>
      {formError && (
        <Banner $tone="bad" role="alert">
          <div>{errorMessage(intl, formError)}</div>
        </Banner>
      )}
      <CardFields
        values={card}
        errors={errors}
        onChange={onChange}
        onBlur={onBlur}
        disabled={submitting}
        ids={{ cardNumber: 'activate-card-number', cvv: 'activate-cvv', expiry: 'activate-expiry' }}
      />
      {CONSENT_ROWS.filter(row => asked[row.key]).map(({ key, id, link: ConsentLink }) => (
        <FormRow key={key}>
          <Checkbox
            label={
              ConsentLink
                ? <FormattedMessage id={id} values={{ link: <ConsentLink /> }} />
                : intl.messages[id]
            }
            labelFor={`activate-card-${key}`}
            id={`activate-card-${key}`}
            value={consents[key]}
            onChange={e => onConsent(key, e.target.checked)}
            required
          />
        </FormRow>
      ))}
      <Actions>
        {actionButton('cancel', onClose)}
        {actionButton('submit', submit, 'primary', { disabled: !canSubmit })}
      </Actions>
    </>
  );

  const renderSubmitting = () => (
    <Centered role="status">
      <Spinner />
      <b>{intl.messages['activate-card-submitting']}</b>
    </Centered>
  );

  const renderDone = already => (
    <>
      <Centered>
        <SuccessMark>
          <CheckMark size={28} />
        </SuccessMark>
        <Title id="activate-card-title" style={{ paddingRight: 0 }}>
          {intl.messages[already ? 'activate-card-already-title' : 'activate-card-success-title']}
        </Title>
        <Hint as="p" $size="14px" style={{ textAlign: 'center' }}>
          {intl.messages[already ? 'activate-card-already-hint' : 'activate-card-success-hint']}
        </Hint>
      </Centered>
      <Actions>{actionButton('done', onClose, 'primary')}</Actions>
    </>
  );

  const renderBody = () => {
    switch (phase) {
    case ACTIVATION_PHASES.SUBMITTING:
      return renderSubmitting();
    case ACTIVATION_PHASES.ACTIVATED:
      return renderDone(false);
    case ACTIVATION_PHASES.ALREADY_ACTIVE:
      return renderDone(true);
    default:
      return renderForm();
    }
  };

  // No close control while the card is being activated: leaving cannot stop it.
  return createPortal(
    <Scrim>
      <Dialog
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="activate-card-title"
      >
        {!submitting && (
          <CloseButton
            type="button"
            aria-label={intl.messages['replace-card-close']}
            onClick={onClose}
          >
            &times;
          </CloseButton>
        )}
        <StepBody>{renderBody()}</StepBody>
      </Dialog>
    </Scrim>,
    document.getElementById('modal-root')
  );
};

ActivateCardModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  phase: PropTypes.string.isRequired,
  card: PropTypes.shape({
    cardNumber: PropTypes.string,
    cvv: PropTypes.string,
    expiry: PropTypes.string,
  }).isRequired,
  errors: PropTypes.shape({
    cardNumber: PropTypes.string,
    cvv: PropTypes.string,
    expiry: PropTypes.string,
  }).isRequired,
  consents: PropTypes.objectOf(PropTypes.bool).isRequired,
  formError: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onConsent: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

ActivateCardModal.defaultProps = {
  formError: null,
};

export default ActivateCardModal;
